import React, { useState, useEffect } from 'react';
import { 
  HardDrive, 
  Folder, 
  FileText, 
  Users, 
  BarChart3, 
  Calendar, 
  Download, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Database,
  Image,
  File,
  Archive
} from 'lucide-react';
import { SistemaService } from '../services';
import toast from 'react-hot-toast';

interface SistemaStats {
  armazenamento: {
    tamanho_total: string;
    tamanho_bytes: number;
    total_arquivos: number;
    total_pastas: number;
    caminho: string;
  };
  anexos: {
    total: number;
    por_tipo: Array<{ tipo: string; quantidade: number }>;
    ultimos_30_dias: number;
  };
  tickets: {
    total: number;
    ultimos_30_dias: number;
  };
  usuarios: {
    total: number;
    ultimos_30_dias: number;
  };
  departamentos: {
    total: number;
    tickets_por_departamento: Array<{ 
      departamento: string; 
      total_tickets: number;
      tickets_abertos: number;
      tickets_em_andamento: number;
      tickets_finalizados: number;
      total_arquivos: number;
    }>;
  };
  unidades: {
    total: number;
    tickets_por_unidade: Array<{ 
      unidade: string; 
      total_tickets: number;
      tickets_abertos: number;
      tickets_em_andamento: number;
      tickets_finalizados: number;
      total_arquivos: number;
    }>;
  };
}

interface AnexosPorPeriodo {
  data: string;
  total_arquivos: number;
  imagens: number;
  documentos: number;
  outros: number;
}

interface PastaAnexo {
  ticket_id: number;
  ticket_numero: string;
  ticket_assunto: string;
  ticket_data: string;
  usuario_nome: string;
  departamento_nome: string;
  total_arquivos_fs: number;
  total_arquivos_bd: number;
  tamanho_total: string;
  tamanho_bytes: number;
  caminho: string;
  is_orfao: boolean;
}

interface PaginacaoInfo {
  total: number;
  limite: number;
  offset: number;
  total_paginas: number;
  pagina_atual: number;
}

const SistemaStats: React.FC = () => {
  const [stats, setStats] = useState<SistemaStats | null>(null);
  const [anexosPeriodo, setAnexosPeriodo] = useState<AnexosPorPeriodo[]>([]);
  const [pastasAnexos, setPastasAnexos] = useState<PastaAnexo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAnexos, setIsLoadingAnexos] = useState(false);
  const [isLoadingPastas, setIsLoadingPastas] = useState(false);
  const [periodo, setPeriodo] = useState('30');
  const [tipoAnexo, setTipoAnexo] = useState('todos');
  const [activeTab, setActiveTab] = useState<'overview' | 'anexos' | 'pastas'>('overview');
  
  // Estados de paginação
  const [paginacaoAnexos, setPaginacaoAnexos] = useState<PaginacaoInfo>({
    total: 0,
    limite: 50,
    offset: 0,
    total_paginas: 0,
    pagina_atual: 1
  });
  
  const [paginacaoPastas, setPaginacaoPastas] = useState<PaginacaoInfo>({
    total: 0,
    limite: 50,
    offset: 0,
    total_paginas: 0,
    pagina_atual: 1
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      const response = await SistemaService.getSistemaEstatisticas();
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas do sistema');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnexosPorPeriodo = async (offset = 0) => {
    try {
      setIsLoadingAnexos(true);
      const response = await SistemaService.getAnexosPorPeriodo({ 
        periodo, 
        tipo: tipoAnexo,
        limite: paginacaoAnexos.limite,
        offset 
      });
      setAnexosPeriodo(response.data);
      setPaginacaoAnexos({
        total: response.total,
        limite: response.limite,
        offset: response.offset,
        total_paginas: response.total_paginas,
        pagina_atual: response.pagina_atual
      });
    } catch (error) {
      console.error('Erro ao carregar anexos por período:', error);
      toast.error('Erro ao carregar anexos por período');
    } finally {
      setIsLoadingAnexos(false);
    }
  };

  const loadPastasAnexos = async (offset = 0) => {
    try {
      setIsLoadingPastas(true);
      const response = await SistemaService.getPastasAnexos({ 
        limite: paginacaoPastas.limite,
        offset 
      });
      setPastasAnexos(response.data);
      setPaginacaoPastas({
        total: response.total,
        limite: response.limite,
        offset: response.offset,
        total_paginas: response.total_paginas,
        pagina_atual: response.pagina_atual
      });
    } catch (error) {
      console.error('Erro ao carregar pastas de anexos:', error);
      toast.error('Erro ao carregar pastas de anexos');
    } finally {
      setIsLoadingPastas(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'imagem': return <Image className="w-4 h-4" />;
      case 'documento': return <FileText className="w-4 h-4" />;
      case 'outro': return <Archive className="w-4 h-4" />;
      default: return <File className="w-4 h-4" />;
    }
  };


  const handlePageChange = (tab: 'anexos' | 'pastas', page: number) => {
    const offset = (page - 1) * (tab === 'anexos' ? paginacaoAnexos.limite : paginacaoPastas.limite);
    if (tab === 'anexos') {
      loadAnexosPorPeriodo(offset);
    } else {
      loadPastasAnexos(offset);
    }
  };

  const handleLimiteChange = (tab: 'anexos' | 'pastas', limite: number) => {
    if (tab === 'anexos') {
      setPaginacaoAnexos(prev => ({ ...prev, limite, offset: 0 }));
      loadAnexosPorPeriodo(0);
    } else {
      setPaginacaoPastas(prev => ({ ...prev, limite, offset: 0 }));
      loadPastasAnexos(0);
    }
  };

  const PaginacaoComponent = ({ paginacao, onPageChange, onLimiteChange, tab }: {
    paginacao: PaginacaoInfo;
    onPageChange: (page: number) => void;
    onLimiteChange: (limite: number) => void;
    tab: 'anexos' | 'pastas';
  }) => {
    if (paginacao.total_paginas <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, paginacao.pagina_atual - 2);
    const endPage = Math.min(paginacao.total_paginas, paginacao.pagina_atual + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Itens por página:</span>
          <select
            value={paginacao.limite}
            onChange={(e) => onLimiteChange(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            Página {paginacao.pagina_atual} de {paginacao.total_paginas} ({paginacao.total} itens)
          </span>
          
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(1)}
              disabled={paginacao.pagina_atual === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              «
            </button>
            <button
              onClick={() => onPageChange(paginacao.pagina_atual - 1)}
              disabled={paginacao.pagina_atual === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ‹
            </button>
            
            {pages.map(page => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-2 py-1 text-sm border rounded ${
                  page === paginacao.pagina_atual
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => onPageChange(paginacao.pagina_atual + 1)}
              disabled={paginacao.pagina_atual === paginacao.total_paginas}
              className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ›
            </button>
            <button
              onClick={() => onPageChange(paginacao.total_paginas)}
              disabled={paginacao.pagina_atual === paginacao.total_paginas}
              className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              »
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-gray-600">Erro ao carregar estatísticas do sistema</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Estatísticas do Sistema
            </h3>
            <p className="text-gray-600">
              Monitoramento de armazenamento, anexos e dados do sistema
            </p>
          </div>
          <button
            onClick={loadStats}
            className="btn btn-outline flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-soft">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Visão Geral</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('anexos')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'anexos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Anexos por Período</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pastas')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pastas'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Folder className="h-5 w-5" />
                <span>Pastas de Anexos</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Tab Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Cards de Estatísticas */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Armazenamento */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 mb-1">Armazenamento</p>
                      <p className="text-2xl font-bold text-blue-900">{stats.armazenamento.tamanho_total}</p>
                      <p className="text-xs text-blue-600">{stats.armazenamento.total_arquivos} arquivos</p>
                    </div>
                    <HardDrive className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                {/* Anexos */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Total de Anexos</p>
                      <p className="text-2xl font-bold text-green-900">{stats.anexos.total.toLocaleString()}</p>
                      <p className="text-xs text-green-600">+{stats.anexos.ultimos_30_dias} últimos 30 dias</p>
                    </div>
                    <FileText className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                {/* Tickets */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600 mb-1">Total de Tickets</p>
                      <p className="text-2xl font-bold text-purple-900">{stats.tickets.total.toLocaleString()}</p>
                      <p className="text-xs text-purple-600">+{stats.tickets.ultimos_30_dias} últimos 30 dias</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-purple-600" />
                  </div>
                </div>

                {/* Usuários */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600 mb-1">Usuários</p>
                      <p className="text-2xl font-bold text-orange-900">{stats.usuarios.total}</p>
                      <p className="text-xs text-orange-600">+{stats.usuarios.ultimos_30_dias} últimos 30 dias</p>
                    </div>
                    <Users className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Anexos por Tipo */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Anexos por Tipo
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stats.anexos.por_tipo.map((item, index) => (
                    <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-center mb-2">
                        {getTipoIcon(item.tipo)}
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{item.quantidade.toLocaleString()}</p>
                      <p className="text-sm text-gray-600 capitalize">{item.tipo}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Departamentos */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Tickets por Departamento
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Departamento</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Abertos</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Em Andamento</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Finalizados</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Arquivos</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stats.departamentos.tickets_por_departamento.slice(0, 10).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{item.departamento || 'Sem Departamento'}</td>
                          <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">{item.total_tickets}</td>
                          <td className="px-4 py-3 text-center text-sm text-blue-600">{item.tickets_abertos}</td>
                          <td className="px-4 py-3 text-center text-sm text-yellow-600">{item.tickets_em_andamento}</td>
                          <td className="px-4 py-3 text-center text-sm text-green-600">{item.tickets_finalizados}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-600">{item.total_arquivos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Unidades */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Tickets por Unidade
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidade</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Abertos</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Em Andamento</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Finalizados</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Arquivos</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stats.unidades.tickets_por_unidade.slice(0, 10).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{item.unidade || 'Sem Unidade'}</td>
                          <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">{item.total_tickets}</td>
                          <td className="px-4 py-3 text-center text-sm text-blue-600">{item.tickets_abertos}</td>
                          <td className="px-4 py-3 text-center text-sm text-yellow-600">{item.tickets_em_andamento}</td>
                          <td className="px-4 py-3 text-center text-sm text-green-600">{item.tickets_finalizados}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-600">{item.total_arquivos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Tab Anexos por Período */}
          {activeTab === 'anexos' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Anexos por Período
                </h4>
                <div className="flex gap-4">
                  <select
                    value={periodo}
                    onChange={(e) => setPeriodo(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="7">Últimos 7 dias</option>
                    <option value="30">Últimos 30 dias</option>
                    <option value="90">Últimos 90 dias</option>
                    <option value="365">Último ano</option>
                  </select>
                  <select
                    value={tipoAnexo}
                    onChange={(e) => setTipoAnexo(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="todos">Todos os tipos</option>
                    <option value="imagem">Imagens</option>
                    <option value="documento">Documentos</option>
                    <option value="outro">Outros</option>
                  </select>
                  <button
                    onClick={() => loadAnexosPorPeriodo()}
                    disabled={isLoadingAnexos}
                    className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {isLoadingAnexos ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <BarChart3 className="w-4 h-4" />
                    )}
                    Carregar
                  </button>
                </div>
              </div>

              {anexosPeriodo.length > 0 ? (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagens</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outros</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {anexosPeriodo.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(item.data).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.total_arquivos}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.imagens}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.documentos}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.outros}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <PaginacaoComponent
                    paginacao={paginacaoAnexos}
                    onPageChange={(page) => handlePageChange('anexos', page)}
                    onLimiteChange={(limite) => handleLimiteChange('anexos', limite)}
                    tab="anexos"
                  />
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum dado encontrado para o período selecionado</p>
                </div>
              )}
            </div>
          )}

          {/* Tab Pastas de Anexos */}
          {activeTab === 'pastas' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Folder className="w-5 h-5" />
                  Pastas de Anexos
                </h4>
                <button
                  onClick={() => loadPastasAnexos()}
                  disabled={isLoadingPastas}
                  className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {isLoadingPastas ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Folder className="w-4 h-4" />
                  )}
                  Carregar Pastas
                </button>
              </div>

              {pastasAnexos.length > 0 ? (
                <>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assunto</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Arquivos</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamanho</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {pastasAnexos.map((pasta, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <span className="text-sm font-medium text-gray-900">{pasta.ticket_numero}</span>
                                  {pasta.is_orfao && (
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 ml-2" />
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-xs truncate" title={pasta.ticket_assunto}>
                                  {pasta.ticket_assunto}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{pasta.usuario_nome}</div>
                                <div className="text-xs text-gray-500">{pasta.departamento_nome}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-900">{pasta.total_arquivos_fs}</span>
                                  {pasta.total_arquivos_fs !== pasta.total_arquivos_bd && (
                                    <span className="text-xs text-yellow-600" title="Diferença entre arquivos no sistema de arquivos e banco de dados">
                                      ({pasta.total_arquivos_bd} BD)
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {pasta.tamanho_total}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(pasta.ticket_data).toLocaleDateString('pt-BR')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <PaginacaoComponent
                    paginacao={paginacaoPastas}
                    onPageChange={(page) => handlePageChange('pastas', page)}
                    onLimiteChange={(limite) => handleLimiteChange('pastas', limite)}
                    tab="pastas"
                  />
                </>
              ) : (
                <div className="text-center py-8">
                  <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhuma pasta de anexos encontrada</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SistemaStats;
